// What the admin knows about each content type: how to list it and which form
// fields edit it. Field names match the API schemas in @mlopsmena/content;
// the API re-validates every save with those same schemas.

import {
  ACCENTS, FAQ_SCOPES, PLATFORMS, ROADMAP_ACCENTS, TIERS,
  type ContentType,
} from '@mlopsmena/content'
import type { Item } from './api'

export type FieldDef =
  | { kind: 'text' | 'url' | 'email' | 'date' | 'textarea'; name: string; label: string; help?: string; required?: boolean }
  | { kind: 'number'; name: string; label: string; step?: number; help?: string }
  | { kind: 'boolean'; name: string; label: string; help?: string }
  | { kind: 'select'; name: string; label: string; options: readonly string[]; help?: string }
  /** Translatable text: one input per language. */
  | { kind: 'l10n'; name: string; label: string; multiline?: boolean; optional?: boolean; help?: string }
  /** Ordered list of translatable lines. */
  | { kind: 'l10nList'; name: string; label: string; multiline?: boolean; help?: string }
  /** Plain strings, one per line (tool names, tags). */
  | { kind: 'stringList'; name: string; label: string; help?: string }
  | { kind: 'media'; name: string; label: string; folder: 'partners' | 'team' | 'misc'; help?: string }
  | { kind: 'datetime'; name: string; label: string; help?: string }
  /** A slug reference to another type's item, chosen from a list of them. */
  | { kind: 'ref'; to: 'partners'; name: string; label: string; help?: string }
  | {
      kind: 'objects'
      name: string
      label: string
      fields: FieldDef[]
      blank: () => Record<string, unknown>
      title: (v: Record<string, unknown>, i: number) => string
      help?: string
    }
  /** Roadmap markdown plus the Arabic names of its phase headings. */
  | { kind: 'roadmapBody' }
  | { kind: 'section'; title: string; fields: FieldDef[] }

export type TypeConfig = {
  type: ContentType
  label: string
  singular: string
  /** Lists ordered by hand (drag to reorder); others sort by date. */
  reorder: boolean
  title: (i: Item) => string
  subtitle?: (i: Item) => string
  /** DTO path the slug is suggested from on new items. */
  slugFrom: string
  fields: FieldDef[]
  /** English-by-design fields, skipped by the "AR missing" count. */
  englishOnly?: string[]
}

type L = { en: string; ar: string | null }
const en = (v: unknown) => (v as L | null)?.en ?? ''
const L0 = () => ({ en: '', ar: '' })

export const TYPES: Record<ContentType, TypeConfig> = {
  partners: {
    type: 'partners', label: 'Partners', singular: 'partner', reorder: true, slugFrom: 'name',
    title: (i) => String(i.name), subtitle: (i) => en(i.role),
    fields: [
      { kind: 'text', name: 'name', label: 'Name', required: true },
      { kind: 'l10n', name: 'role', label: 'Role', help: 'e.g. "Industry Partner" — shown on the logo card.' },
      { kind: 'l10n', name: 'blurb', label: 'Blurb', multiline: true, optional: true },
      { kind: 'url', name: 'href', label: 'Website', required: true },
      { kind: 'media', name: 'logo', label: 'Logo', folder: 'partners', help: 'Without a logo the name is shown as text.' },
      { kind: 'select', name: 'accent', label: 'Accent colour', options: ACCENTS },
    ],
  },

  articles: {
    type: 'articles', label: 'Articles', singular: 'article', reorder: false, slugFrom: 'title.en',
    title: (i) => en(i.title), subtitle: (i) => `${i.date} · ${i.platform}`,
    fields: [
      { kind: 'url', name: 'href', label: 'Article link', required: true },
      { kind: 'l10n', name: 'title', label: 'Title' },
      { kind: 'l10n', name: 'description', label: 'Description', multiline: true },
      { kind: 'date', name: 'date', label: 'Published on', required: true },
      { kind: 'select', name: 'platform', label: 'Platform', options: PLATFORMS },
      { kind: 'stringList', name: 'tags', label: 'Tags', help: 'One per line, lowercase.' },
      { kind: 'text', name: 'internalHref', label: 'Also on this site at', help: 'e.g. /roadmaps/devops-to-mlops' },
    ],
  },

  team: {
    type: 'team', label: 'Team', singular: 'team member', reorder: true, slugFrom: 'name',
    title: (i) => String(i.name), subtitle: (i) => `${i.tier} · ${en(i.role)}`,
    fields: [
      { kind: 'text', name: 'name', label: 'Name', required: true },
      { kind: 'text', name: 'initials', label: 'Initials', required: true, help: 'Shown when there is no photo.' },
      { kind: 'select', name: 'tier', label: 'Tier', options: TIERS },
      { kind: 'number', name: 'axis', label: 'Axis number', help: 'Leads only.' },
      { kind: 'l10n', name: 'role', label: 'Role' },
      { kind: 'l10n', name: 'bio', label: 'Bio', multiline: true },
      { kind: 'media', name: 'photo', label: 'Photo', folder: 'team' },
      { kind: 'url', name: 'linkedin', label: 'LinkedIn' },
      { kind: 'email', name: 'email', label: 'Email', help: 'The @mlopsmena.com address.' },
    ],
  },

  faqs: {
    type: 'faqs', label: 'FAQ', singular: 'question', reorder: true, slugFrom: 'q.en',
    title: (i) => en(i.q), subtitle: (i) => String(i.scope),
    fields: [
      { kind: 'select', name: 'scope', label: 'Shown on', options: FAQ_SCOPES, help: '"course" questions are grouped under "About the courses" on the FAQ page.' },
      { kind: 'l10n', name: 'q', label: 'Question' },
      { kind: 'l10n', name: 'a', label: 'Answer', multiline: true },
    ],
  },

  roadmaps: {
    type: 'roadmaps', label: 'Roadmaps', singular: 'roadmap', reorder: true, slugFrom: 'title.en',
    title: (i) => en(i.title), subtitle: (i) => en(i.level),
    fields: [
      { kind: 'l10n', name: 'title', label: 'Title' },
      { kind: 'l10n', name: 'tagline', label: 'Tagline' },
      { kind: 'section', title: 'At a glance', fields: [
        { kind: 'l10n', name: 'level', label: 'Level' },
        { kind: 'l10n', name: 'duration', label: 'Duration' },
        { kind: 'l10n', name: 'commitment', label: 'Commitment' },
        { kind: 'l10n', name: 'audience', label: 'Audience', multiline: true },
      ] },
      { kind: 'select', name: 'accent', label: 'Accent colour', options: ROADMAP_ACCENTS },
      { kind: 'date', name: 'published', label: 'Published on', required: true },
      { kind: 'url', name: 'sourceUrl', label: 'Original article' },
      { kind: 'roadmapBody' },
    ],
  },
}

/** Sidebar order. */
export const TYPE_ORDER: ContentType[] = ['roadmaps', 'articles', 'team', 'faqs', 'partners']

export const SETTINGS_FIELDS: FieldDef[] = [
  { kind: 'l10n', name: 'teamSummary', label: 'Team page summary', optional: true },
  { kind: 'section', title: 'Community numbers (home page)', fields: [
    { kind: 'number', name: 'statWhatsapp', label: 'WhatsApp members', help: 'Shown as typed. Empty keeps the default.' },
    { kind: 'number', name: 'statLinkedin', label: 'LinkedIn followers', help: 'Shown as typed. Empty keeps the default.' },
    { kind: 'number', name: 'statStudents', label: 'Course students', help: 'Shown as typed. Empty keeps the default.' },
    { kind: 'number', name: 'statDiscord', label: 'Discord members', help: 'Updates itself every 3 hours; this is the fallback when Discord cannot be reached.' },
    { kind: 'number', name: 'statYtViews', label: 'YouTube views', help: 'Updates itself every 3 hours (needs the YouTube key); this is the fallback.' },
    { kind: 'number', name: 'statYtSubs', label: 'YouTube subscribers', help: 'Updates itself every 3 hours (needs the YouTube key); this is the fallback.' },
  ] },
]
